import React from 'react'
import { cn } from '@/lib/utils'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  className
}: PaginationProps) {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)

  // Show max 7 pages (current + 3 before + 3 after)
  const visiblePages = pages.filter(page => {
    if (totalPages <= 7) return true
    if (page === 1 || page === totalPages) return true
    return Math.abs(page - currentPage) <= 1
  })

  return (
    <div className={cn('flex items-center space-x-1', className)}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className="p-2 rounded-monumenta hover:bg-brand-dark-lighter disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4 text-brand-gray" />
      </button>

      {visiblePages.map((page, index) => {
        const prevPage = visiblePages[index - 1]
        const showEllipsis = prevPage && page - prevPage > 1

        return (
          <React.Fragment key={page}>
            {showEllipsis && (
              <span className="px-2 py-1 text-brand-gray">...</span>
            )}
            <button
              onClick={() => onPageChange(page)}
              className={cn(
                'px-3 py-1 rounded-monumenta text-sm font-medium transition-colors',
                page === currentPage
                  ? 'bg-brand-primary text-brand-dark'
                  : 'text-brand-gray hover:bg-brand-dark-lighter hover:text-white'
              )}
            >
              {page}
            </button>
          </React.Fragment>
        )
      })}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className="p-2 rounded-monumenta hover:bg-brand-dark-lighter disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight className="w-4 h-4 text-brand-gray" />
      </button>
    </div>
  )
}